import clsx from "clsx";
import React from "react";

interface FaqCategorySelectorProps {
  categories: FaqCategoryOption[];
  activeCategoryKey: string;
  onSelect: (categoryKey: string) => void;
}

export interface FaqCategoryOption {
  key: string;
  label: string;
  description: string;
  questionIds: number[];
  icon: React.ComponentType<{ className?: string }>;
}

const getQuestionLabel = (count: number) => {
  if (count === 1) {
    return "1 pytanie";
  }

  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;

  if (
    lastDigit >= 2 &&
    lastDigit <= 4 &&
    (lastTwoDigits < 12 || lastTwoDigits > 14)
  ) {
    return `${count} pytania`;
  }

  return `${count} pytań`;
};

const FaqCategorySelector: React.FC<FaqCategorySelectorProps> = ({
  categories,
  activeCategoryKey,
  onSelect,
}) => {
  return (
    <div className="mb-10 w-full">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {categories.map((category) => {
          const isActive = category.key === activeCategoryKey;
          const IconComponent = category.icon;

          return (
            <button
              key={category.key}
              type="button"
              onClick={() => onSelect(category.key)}
              aria-pressed={isActive}
              className={clsx(
                "group relative flex min-h-[12rem] flex-col items-center justify-between overflow-hidden rounded border p-6 text-center shadow-sm transition duration-300",
                isActive
                  ? "scale-[1.01] border-[#00A87A] bg-[#E8FAF4] text-[#0C1F3F] shadow-md"
                  : "border-[#E5E7EB] bg-white text-[#0C1F3F] hover:scale-[1.01] hover:border-[#00A87A] hover:bg-[#E8FAF4] hover:text-[#0C1F3F] hover:shadow-md"
              )}
            >
              <IconComponent
                className={clsx(
                  "h-11 w-11 transition-colors",
                  isActive
                    ? "text-[#00A87A]"
                    : "text-[#5C6370] group-hover:text-[#00A87A]"
                )}
              />
              <div className="space-y-2">
                <div className="text-lg font-semibold leading-snug">
                  {category.label}
                </div>
                <p
                  className={clsx(
                    "text-sm leading-relaxed",
                    isActive
                      ? "text-[#5C6370]"
                      : "text-[#5C6370] group-hover:text-[#5C6370]"
                  )}
                >
                  {category.description}
                </p>
              </div>
              <div
                className={clsx(
                  "text-xs font-semibold uppercase tracking-[0.2em]",
                  isActive
                    ? "text-[#00A87A]"
                    : "text-[#00A87A] group-hover:text-[#00A87A]"
                )}
              >
                {getQuestionLabel(category.questionIds.length)}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default FaqCategorySelector;
